import { z } from 'zod';
import { optionalRefSchema } from './schemas';

/** Edition of the contest rules and personal-data consent stored with every entry. */
export const giveawayConsentVersion = '2026-10-10.2';
/** Telegram channel of «Изнанка» for public links (results, news). */
export const giveawayChannelUrl = 'https://t.me/iznanka_krd';
export const giveawayLimits = { title: 160, prizeText: 1000, description: 2000, name: 100, social: 100, source: 60, winners: 100 } as const;

export const giveawayNetworks = ['telegram', 'max'] as const;
export type GiveawayNetwork = (typeof giveawayNetworks)[number];
export const giveawayNetworkLabels: Record<GiveawayNetwork, string> = { telegram: 'Telegram', max: 'MAX' };

/**
 * `pending` — the participant has not opened the bot yet (no platform user id), `subscribed` /
 * `not_subscribed` — result of the last channel membership check, `error` — the check itself failed.
 */
export const giveawaySubscriptionStatuses = ['pending', 'subscribed', 'not_subscribed', 'error'] as const;
export type GiveawaySubscriptionStatus = (typeof giveawaySubscriptionStatuses)[number];
export const giveawaySubscriptionLabels: Record<GiveawaySubscriptionStatus, string> = {
  subscribed: '✅ подписан', not_subscribed: '❌ не подписан', pending: '⏳ не прошёл бота', error: '⚠ ошибка проверки',
};

/** Bot start parameter carrying the entry's one-time link token: `gw_` + 32 hex characters. */
export const giveawayStartPrefix = 'gw_';
export const giveawayLinkTokenPattern = /^[0-9a-f]{32}$/;

/**
 * Bot link; with a token it opens the bot with the entry's token (Telegram `?start=`). MAX entries are linked by
 * the phone shared with the bot, so their link carries no token.
 */
export function giveawayBotUrl(network: GiveawayNetwork, botUsername: string, token?: string): string {
  const host = network === 'telegram' ? 'https://t.me' : 'https://max.ru';
  return `${host}/${encodeURIComponent(botUsername)}${token ? `?start=${giveawayStartPrefix}${token}` : ''}`;
}

const normalizedText = (minimum: number, maximum: number, message: string) => z.string()
  .transform((value) => value.trim().replace(/\s+/g, ' '))
  .pipe(z.string().min(minimum, message).max(maximum, `Максимум ${maximum} символов`));

/** The lead form rule: `+7…` or `8…`, 11 digits, separators allowed. Returns `7XXXXXXXXXX` or null. */
export function normalizeGiveawayPhone(value: string): string | null {
  const trimmed = value.trim();
  if (!/^(\+7|8)[\d\s()\-]+$/.test(trimmed)) return null;
  const digits = trimmed.replace(/\D/g, '');
  return digits.length === 11 ? `7${digits.slice(1)}` : null;
}

export type GiveawaySocial = { handle: string; url: string };

/**
 * Loose check and normalisation of «Ссылка или ник».
 * Telegram: `@user`, `user`, `t.me/user` (or telegram.me) → `@user` + `https://t.me/user`, lowercased.
 * MAX: profile link `max.ru/u/<id>` (case kept) → `u/<id>`, or a nick / `max.ru/<nick>` → `@nick` + `https://max.ru/nick`.
 */
export function normalizeGiveawaySocial(network: GiveawayNetwork, value: string): GiveawaySocial | null {
  const trimmed = value.trim();
  if (network === 'telegram') {
    const username = (/^(?:https?:\/\/)?(?:www\.)?(?:t\.me|telegram\.me)\/([^/?#\s]+)\/?(?:[?#].*)?$/i.exec(trimmed) ?? /^@?([^/\s]+)$/.exec(trimmed))?.[1];
    if (!username || !/^[a-zA-Z][a-zA-Z0-9_]{3,31}$/.test(username)) return null;
    const lower = username.toLowerCase();
    return { handle: `@${lower}`, url: `https://t.me/${lower}` };
  }
  const link = /^(?:https?:\/\/)?(?:www\.)?max\.ru\/([^?#\s]+?)\/?(?:[?#].*)?$/i.exec(trimmed);
  const path = link ? link[1] : trimmed.replace(/^@/, '');
  const profile = /^u\/([A-Za-z0-9_-]{6,90})$/.exec(path);
  if (link && profile) return { handle: `u/${profile[1]}`, url: `https://max.ru/u/${profile[1]}` };
  if (!/^[a-zA-Z][a-zA-Z0-9_.]{1,63}$/.test(path)) return null;
  const lower = path.toLowerCase();
  return { handle: `@${lower}`, url: `https://max.ru/${lower}` };
}

/** Entry source from the tab's first-touch `utm_source/utm_content` (e.g. `vk/krasnodar_live`): lowercased, safe characters only, ≤60. */
export function sanitizeGiveawaySource(value: string | null | undefined): string | undefined {
  const cleaned = (value ?? '').trim().toLowerCase().replace(/[^a-z0-9_.\-/]/g, '').slice(0, giveawayLimits.source);
  return cleaned || undefined;
}

const socialHints: Record<GiveawayNetwork, string> = {
  telegram: 'Ник в Telegram (@username) или ссылка t.me/username',
  max: 'Ссылка на профиль MAX (max.ru/…) или ник',
};

/** `POST /api/giveaway/entries` from the contest popup; `website` is the bot honeypot and must stay empty, `ref` is the first-touch attribution. */
export const giveawayEntrySchema = z.object({
  name: normalizedText(2, giveawayLimits.name, 'Укажите имя'),
  phone: z.string().trim().min(1, 'Укажите телефон').refine((value) => normalizeGiveawayPhone(value) !== null, 'Телефон в формате +7… или 8… (11 цифр)'),
  network: z.enum(giveawayNetworks, { errorMap: () => ({ message: 'Выберите Telegram или MAX' }) }),
  /** Telegram only: MAX participants are identified by the phone number they share with the MAX bot. */
  social: z.string().trim().max(giveawayLimits.social).optional().default(''),
  rulesAccepted: z.literal(true, { errorMap: () => ({ message: 'Подтвердите, что ознакомились с правилами' }) }),
  privacyAccepted: z.literal(true, { errorMap: () => ({ message: 'Нужно согласие на обработку данных' }) }),
  website: z.string().max(0).optional().default(''),
  source: z.string().max(giveawayLimits.source).regex(/^[a-z0-9_.\-/]+$/).optional(),
  ref: optionalRefSchema,
}).strict().superRefine((value, context) => {
  if (value.network === 'telegram' && normalizeGiveawaySocial(value.network, value.social) === null) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['social'], message: value.social ? socialHints.telegram : 'Укажите ссылку или ник' });
  }
});
export type GiveawayEntryInput = z.input<typeof giveawayEntrySchema>;

/** `201` of `POST /api/giveaway/entries`. */
export type GiveawayEntryResponse = { id: number; network: GiveawayNetwork; botUrl: string; channelUrl: string };
/**
 * `409` of `POST /api/giveaway/entries` for a phone or account already entered: one message whichever collided,
 * never the existing entry's token. MAX gets the (token-free) bot link to finish by sharing the phone.
 */
export type GiveawayAlreadyEntered = { error: { code: 'ALREADY_ENTERED'; message: string }; botUrl?: string };

const isoDateTime = z.string().datetime({ message: 'Укажите дату и время' });

/** `PUT /api/admin/giveaway`: dates are UTC ISO strings (the admin enters Moscow time). */
export const giveawaySettingsSchema = z.object({
  active: z.boolean(),
  title: normalizedText(3, giveawayLimits.title, 'Укажите заголовок'),
  prizeText: z.string().trim().min(3, 'Опишите приз').max(giveawayLimits.prizeText, `Максимум ${giveawayLimits.prizeText} символов`),
  description: z.string().trim().max(giveawayLimits.description, `Максимум ${giveawayLimits.description} символов`).optional().default(''),
  endsAt: isoDateTime.nullable(),
  drawAt: isoDateTime.nullable(),
  winnersCount: z.number({ invalid_type_error: 'Укажите число' }).int('Целое число').min(1, 'От 1 до 100').max(giveawayLimits.winners, 'От 1 до 100'),
}).strict().refine((value) => !value.endsAt || !value.drawAt || Date.parse(value.drawAt) >= Date.parse(value.endsAt), {
  message: 'Итоги конкурса не могут быть раньше окончания приёма заявок',
  path: ['drawAt'],
});
export type GiveawaySettingsInput = z.input<typeof giveawaySettingsSchema>;

/** `PATCH /api/admin/giveaway/entries/:id`: exclusion and/or the admin's manual subscription mark. */
export const giveawayEntryUpdateSchema = z.object({ disqualified: z.boolean().optional(), manualVerified: z.boolean().optional() }).strict()
  .refine((value) => value.disqualified !== undefined || value.manualVerified !== undefined, { message: 'Нечего изменять' });
/** Response of that PATCH: the entry and the counts it changed (eligible, manual…). */
export type GiveawayEntryUpdateResponse = { item: GiveawayEntry; counts: GiveawayCounts };
export const giveawayDrawSchema = z.object({ force: z.boolean().optional().default(false) }).strict();

export type GiveawaySettings = {
  active: boolean;
  title: string;
  prizeText: string;
  description: string;
  endsAt: string | null;
  drawAt: string | null;
  winnersCount: number;
  updatedAt: string;
  updatedBy: string | null;
};

/** A network the popup offers: its bot is running and its channel is configured. */
export type GiveawayPublicNetwork = { network: GiveawayNetwork; channelUrl: string };

/** What public pages render; `entriesOpen` and `networks` are computed by the server at render time. */
export type GiveawayPublic = {
  active: boolean;
  title: string;
  prizeText: string;
  description: string;
  endsAt: string | null;
  drawAt: string | null;
  winnersCount: number;
  entriesOpen: boolean;
  networks: GiveawayPublicNetwork[];
};

export type GiveawayEntry = {
  id: number;
  name: string;
  /** `7XXXXXXXXXX`; null only for entries made before the contest rework with a nick instead of a phone. */
  phone: string | null;
  /** Null only for pre-rework entries made with a phone. */
  network: GiveawayNetwork | null;
  socialHandle: string | null;
  socialUrl: string | null;
  /** The participant opened the bot, so the platform user id is known. */
  linked: boolean;
  subscriptionStatus: GiveawaySubscriptionStatus;
  checkedAt: string | null;
  checkError: string | null;
  source: string | null;
  createdAt: string;
  consentVersion: string;
  winnerAt: string | null;
  disqualified: boolean;
  /** The admin confirmed the subscription by hand: counts as subscribed at the draw without a bot check. */
  manualVerifiedAt: string | null;
  manualVerifiedBy: string | null;
};

export type GiveawayCounts = {
  total: number;
  subscribed: number;
  notSubscribed: number;
  pending: number;
  error: number;
  disqualified: number;
  winners: number;
  /** Subscription confirmed manually by the admin. */
  manual: number;
  /**
   * Draw candidates: not disqualified, not yet a winner, and either confirmed in the bot (account known; the
   * draw checks the subscription live) or verified manually (counted as subscribed without a check).
   */
  eligible: number;
};

/** Background re-check of all linked entries started by «Проверить подписки». */
export type GiveawayCheckProgress = {
  running: boolean;
  total: number;
  done: number;
  subscribed: number;
  notSubscribed: number;
  errors: number;
  startedAt: string | null;
  finishedAt: string | null;
};

/** What the draw's live subscription check found among the candidates (not disqualified, not yet winners). */
export type GiveawayDrawSummary = {
  /** Candidates whose subscription was checked live at the draw (manually verified ones are not checked). */
  checked: number;
  /** Manually verified by the admin: counted as subscribed without a check. */
  manual: number;
  /** Subscribed at the draw (live check or manual mark): the pool the winners were picked from. */
  subscribed: number;
  excludedNotSubscribed: number;
  /** The check failed (API error, bot of that network not running): excluded from this draw. */
  excludedError: number;
  /** Never confirmed in the bot, so the account is unknown and cannot be checked. */
  excludedUnconfirmed: number;
  /** Distinct check error messages (up to 5) explaining `excludedError`. */
  errors: string[];
};

/**
 * Background draw: re-checks every candidate's subscription live (sequentially), then picks winners among
 * those subscribed now. `summary` and `winners`/`error` are set once it has finished.
 */
export type GiveawayDrawProgress = {
  running: boolean;
  total: number;
  done: number;
  startedAt: string | null;
  finishedAt: string | null;
  summary: GiveawayDrawSummary | null;
  winners: GiveawayEntry[];
  error: { code: 'NO_ELIGIBLE_ENTRIES' | 'DRAW_FAILED'; message: string } | null;
};

export type GiveawayAdminData = {
  settings: GiveawaySettings;
  entriesOpen: boolean;
  entries: GiveawayEntry[];
  bySource: Array<{ source: string | null; count: number }>;
  counts: GiveawayCounts;
  /** Networks whose bot is running and channel is configured (offered in the popup). */
  networks: GiveawayNetwork[];
  check: GiveawayCheckProgress;
  draw: GiveawayDrawProgress;
};

export function giveawayEntriesOpen(settings: { active: boolean; endsAt: string | null }, now = Date.now()): boolean {
  return settings.active && (settings.endsAt === null || now < Date.parse(settings.endsAt));
}

const monthsGenitive = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

/**
 * «15 ноября 2026, 20:00 (МСК)». Europe/Moscow is fixed at UTC+3 since 2014; formatting by hand
 * keeps SSR and the hydrating browser byte-identical regardless of ICU data.
 */
export function formatMoscowDateTime(iso: string): string {
  const moscow = new Date(Date.parse(iso) + 3 * 3_600_000);
  const time = `${String(moscow.getUTCHours()).padStart(2, '0')}:${String(moscow.getUTCMinutes()).padStart(2, '0')}`;
  return `${moscow.getUTCDate()} ${monthsGenitive[moscow.getUTCMonth()]} ${moscow.getUTCFullYear()}, ${time} (МСК)`;
}

/** Winner as published in the channel: first name + surname initial, phone (or, for old entries, nick) partially hidden. */
export function publicWinnerLabel(entry: Pick<GiveawayEntry, 'name' | 'phone' | 'socialHandle'>): string {
  const [first = '', second] = entry.name.split(' ');
  const name = second ? `${first} ${second[0]}.` : first;
  const handle = entry.socialHandle ?? '';
  const contact = entry.phone
    ? `+${entry.phone.slice(0, 2)}•••••${entry.phone.slice(-2)}`
    : `${handle.slice(0, 3)}•••${handle.slice(-1)}`;
  return `${name} (${contact})`;
}
